import { Badge, Box, Checkbox, Flex, Icon, Text } from "@chakra-ui/react";
import { Header, MetricProto } from "build-ts/metadata";
import { KGColumnProto } from "build-ts/knowledege_graph";
import React from "react";
import { EntityKind, EntityNameTooltip, getKindIcon } from "./DataScopeLeftPanel";

/** Label, background, text, and icon colors for the entity kind pill (Chakra tokens). */
function entityKindPillStyle(kind: EntityKind): { label: string; bg: string; color: string; iconColor: string } {
  switch (kind) {
    case "metric":
      return { label: "Metric", bg: "transparent", color: "purple.800", iconColor: "purple.600" };
    case "measure":
      return { label: "Measure", bg: "transparent", color: "purple.800", iconColor: "purple.600" };
    case "attribute":
      return { label: "Attribute", bg: "transparent", color: "purple.800", iconColor: "purple.600" };
    case "time":
      return { label: "Datetime", bg: "transparent", color: "purple.800", iconColor: "purple.600" };
    case "benchmark":
      return { label: "Benchmark", bg: "transparent", color: "purple.800", iconColor: "purple.600" };
    default:
      return { label: "—", bg: "gray.100", color: "gray.800", iconColor: "gray.600" };
  }
}

const EntityLeftPanelCard: React.FC<{
  header: Header;
  kind: EntityKind;
  onToggle: () => void;
  isSelected: boolean;
  isDisabled?: boolean;
  dataModelList: Header[];
  metricProtoMap: Map<string, MetricProto>;
  kgColumnProtoMap: Map<string, KGColumnProto>;
}> = ({ header, kind, onToggle, isSelected, isDisabled = false, dataModelList, metricProtoMap, kgColumnProtoMap }) => {
  return (
    <EntityNameTooltip
      shouldWrapChildren={false}
      header={header}
      kind={kind}
      dataModelList={dataModelList}
      metricProtoMap={metricProtoMap}
      kgColumnProtoMap={kgColumnProtoMap}
    >
      <Flex
        align="center"
        gap={3}
        px={4}
        py={1}
        borderBottomWidth="1px"
        borderColor="gray.50"
        cursor={isDisabled ? "not-allowed" : "pointer"}
        bg={isSelected ? "purple.50" : "transparent"}
        opacity={isDisabled ? 0.45 : 1}
        onClick={() => {
          if (!isDisabled) onToggle();
        }}
        _hover={{ bg: isDisabled ? undefined : (isSelected ? "purple.50" : "gray.50") }}
        w="100%"
        role="group"
      >
        <Flex
          align="center"
          lineHeight={1}
          flexShrink={0}
          sx={{ ".chakra-checkbox__control": { marginBottom: 0 } }}
        >
          <Checkbox
            isChecked={isSelected}
            isDisabled={isDisabled}
            colorScheme="purple"
            pointerEvents="none"
            mb={0}
          />
        </Flex>
        <Box flex="1" minW={0} textAlign="left" pointerEvents="auto">
          <Text
            as="span"
            display="block"
            fontSize="xs"
            fontWeight="600"
            color="gray.900"
            mb={0}
            noOfLines={2}
            textAlign="left"
            lineHeight="short"
          >
            {header.name}
          </Text>
        </Box>
        {kind !== "metric" && (
          <Badge
            fontSize="10px"
            textTransform="none"
            display="inline-flex"
            alignItems="center"
            gap={1}
            pl={1.5}
            pr={2}
            py={0.5}
            flexShrink={0}
            bg={entityKindPillStyle(kind).bg}
            color={entityKindPillStyle(kind).color}
          >
            <Icon as={getKindIcon(kind)} boxSize={3} color={entityKindPillStyle(kind).iconColor} aria-hidden />
            {entityKindPillStyle(kind).label}
          </Badge>
        )}
      </Flex>
    </EntityNameTooltip>
  );
};

export default EntityLeftPanelCard;
